import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KnowledgeApi } from '../apis/knowledge.api';
import { QuickActionsApi } from '../apis/quick-actions.api';
import { IKnowledgeDocument } from '../interfaces/knowledge-document.interface';
import { IQuickActionsResponse } from '../interfaces/quick-action.interface';
import { KnowledgeCacheService } from './knowledge-cache.service';

const KNOWLEDGE_PAGE_SIZE = 100;

/**
 * Busca a lista de documentos e as ações rápidas do compositor. Provido no
 * componente: o popup e a página cheia coexistem, e cada um precisa do seu
 * próprio estado de busca (loading, truncado, invalidação).
 */
@Injectable()
export class ComposerDataStore implements OnDestroy {
  private readonly documentsSubject = new BehaviorSubject<IKnowledgeDocument[]>([]);
  private readonly documentsLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly documentsTruncatedSubject = new BehaviorSubject<boolean>(false);
  private readonly quickActionsSubject = new BehaviorSubject<IQuickActionsResponse | null>(null);
  private readonly quickActionsLoadingSubject = new BehaviorSubject<boolean>(false);

  readonly documents$: Observable<IKnowledgeDocument[]> = this.documentsSubject.asObservable();
  readonly documentsLoading$: Observable<boolean> = this.documentsLoadingSubject.asObservable();
  readonly documentsTruncated$: Observable<boolean> = this.documentsTruncatedSubject.asObservable();
  readonly quickActions$: Observable<IQuickActionsResponse | null> = this.quickActionsSubject.asObservable();
  readonly quickActionsLoading$: Observable<boolean> = this.quickActionsLoadingSubject.asObservable();

  private readonly destroy$ = new Subject<void>();
  // Emite a cada invalidação e a cada nova carga: encerra a busca ainda em voo.
  private readonly reload$ = new Subject<void>();

  get hasQuickActions(): boolean {
    return this.quickActionsSubject.value !== null;
  }

  constructor(
    private knowledgeApi: KnowledgeApi,
    private quickActionsApi: QuickActionsApi,
    private knowledgeCache: KnowledgeCacheService
  ) {
    this.knowledgeCache.invalidated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.discardDocuments());
  }

  loadDocuments(): void {
    this.reload$.next();
    this.documentsLoadingSubject.next(true);
    this.knowledgeApi
      .list({ limit: KNOWLEDGE_PAGE_SIZE })
      .pipe(takeUntil(this.reload$), takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.documentsSubject.next(page.items);
          this.documentsTruncatedSubject.next(!!page.nextCursor);
          this.documentsLoadingSubject.next(false);
        },
        error: () => {
          this.documentsSubject.next([]);
          this.documentsLoadingSubject.next(false);
        }
      });
  }

  loadQuickActions(): void {
    this.quickActionsLoadingSubject.next(true);
    this.quickActionsApi
      .getQuickActions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.quickActionsSubject.next(data);
          this.quickActionsLoadingSubject.next(false);
        },
        error: () => this.quickActionsLoadingSubject.next(false)
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  private discardDocuments(): void {
    this.reload$.next();
    this.documentsSubject.next([]);
    this.documentsTruncatedSubject.next(false);
    this.documentsLoadingSubject.next(false);
  }
}
